import { ValidationOptions, ValidationResult } from '../types/ValidationTypes';

export const LIMITS = {
  email: { min: 3, max: 100 },
  password: { min: 8, max: 40 },
  phoneNumber: { min: 7, max: 20 },
  companyNameMax: 100,
  representativeNameMax: 100,
  businessRegistrationMax: 100,
  addressMax: 255,
  content: { min: 1, max: 20000 },
  receiverNameMax: 100,
} as const;

export const REGEX = {
  email: /^[\w-.]+@[\w-]+\.[a-zA-Z]{2,}$/,
  password: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9])(?!.*\s).{8,40}$/,
  phoneNumber: /^[0-9+()\-\s]{1,20}$/,
} as const;

export const VALIDATION_MESSAGES = {
  email: {
    required: 'Email is required',
    length: (min: number = LIMITS.email.min, max: number = LIMITS.email.max) =>
      `Email must be between ${min} and ${max} characters`,
    invalid: 'Please enter a valid email address',
    notVerified: 'Please verify your email',
  },
  password: {
    required: 'Password is required',
    invalid:
      'Password must be 8-40 characters and include letters, numbers, and special characters.',
  },
  confirmPassword: {
    required: 'Please confirm your password',
    mismatch: 'Passwords do not match',
  },
  phoneNumber: {
    required: 'Phone number is required',
    length: (
      min: number = LIMITS.phoneNumber.min,
      max: number = LIMITS.phoneNumber.max,
    ) => `Phone number must be between ${min} and ${max} characters`,
    invalid: 'Phone number format is invalid',
  },
  companyName: {
    required: 'Company name is required',
    maxLength: (max: number = LIMITS.companyNameMax) =>
      `Company name must be less than ${max} characters`,
  },
  representativeName: {
    required: 'Representative name is required',
    maxLength: (max: number = LIMITS.representativeNameMax) =>
      `Representative name must be less than ${max} characters`,
  },
  businessRegistrationNumber: {
    required: 'Business registration number is required',
    maxLength: (max: number = LIMITS.businessRegistrationMax) =>
      `Business registration number must be less than ${max} characters`,
  },
  address: {
    required: 'Address is required',
    maxLength: (max: number = LIMITS.addressMax) =>
      `Business address must be less than ${max} characters`,
  },
  content: {
    required: 'Content is required',
    length: (
      min: number = LIMITS.content.min,
      max: number = LIMITS.content.max,
    ) => `Content must be between ${min} and ${max} characters.`,
  },
  businessCertificate: {
    required: 'Certificate for Business Registration is required',
  },
  isKoreanCompany: {
    required: 'Please select company location',
  },
  personalInfoAgree: {
    notAgreed: 'You must agree to the Privacy Policy',
  },
  termsOfServiceAgree: {
    notAgreed: 'You must agree to the Terms of Service',
  },
  receiverName: {
    required: 'Receiver Name is required',
    maxLength: (max: number = LIMITS.receiverNameMax) =>
      `Receiver name must be less than ${max} characters`,
  },
  login: {
    invalidCredentials: 'Check your Id or Password',
  },
} as const;

export const validators = {
  email(value: string, options: ValidationOptions = {}): ValidationResult {
    const required = options.required ?? true;
    const trimmed = (value ?? '').trim();

    if (!trimmed) {
      if (!required) return { valid: true, error: '' };
      return { valid: false, error: VALIDATION_MESSAGES.email.required };
    }

    if (
      trimmed.length < LIMITS.email.min ||
      trimmed.length > LIMITS.email.max
    ) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.email.length(),
      };
    }

    if (!REGEX.email.test(trimmed)) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.email.invalid,
      };
    }

    return { valid: true, error: '' };
  },

  password(value: string, options: ValidationOptions = {}): ValidationResult {
    const required = options.required ?? true;
    const password = value ?? '';

    if (!password) {
      if (!required) return { valid: true, error: '' };
      return { valid: false, error: VALIDATION_MESSAGES.password.required };
    }

    if (!REGEX.password.test(password)) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.password.invalid,
      };
    }

    return { valid: true, error: '' };
  },

  confirmPassword(
    password: string,
    confirmPassword: string,
    options: ValidationOptions = {},
  ): ValidationResult {
    const required = options.required ?? true;

    if (!confirmPassword) {
      if (!required) return { valid: true, error: '' };
      return {
        valid: false,
        error: VALIDATION_MESSAGES.confirmPassword.required,
      };
    }

    if (password !== confirmPassword) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.confirmPassword.mismatch,
      };
    }

    return { valid: true, error: '' };
  },

  phoneNumber(
    value: string,
    options: ValidationOptions & {
      minLength?: number;
      maxLength?: number;
      checkPattern?: boolean;
    } = {},
  ): ValidationResult {
    const required = options.required ?? true;
    const minLength = options.minLength ?? LIMITS.phoneNumber.min;
    const maxLength = options.maxLength ?? LIMITS.phoneNumber.max;
    const checkPattern = options.checkPattern ?? true;
    const trimmed = (value ?? '').trim();

    if (!trimmed) {
      if (!required) return { valid: true, error: '' };
      return { valid: false, error: VALIDATION_MESSAGES.phoneNumber.required };
    }

    if (trimmed.length < minLength || trimmed.length > maxLength) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.phoneNumber.length(minLength, maxLength),
      };
    }

    if (checkPattern && !REGEX.phoneNumber.test(trimmed)) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.phoneNumber.invalid,
      };
    }

    return { valid: true, error: '' };
  },

  companyName(
    value: string,
    options: ValidationOptions & { maxLength?: number } = {},
  ): ValidationResult {
    const required = options.required ?? true;
    const maxLength = options.maxLength ?? LIMITS.companyNameMax;
    const trimmed = (value ?? '').trim();

    if (!trimmed) {
      if (!required) return { valid: true, error: '' };
      return {
        valid: false,
        error: VALIDATION_MESSAGES.companyName.required,
      };
    }

    if (trimmed.length > maxLength) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.companyName.maxLength(maxLength),
      };
    }

    return { valid: true, error: '' };
  },

  representativeName(
    value: string,
    options: ValidationOptions & { maxLength?: number } = {},
  ): ValidationResult {
    const required = options.required ?? true;
    const maxLength = options.maxLength ?? LIMITS.representativeNameMax;
    const trimmed = (value ?? '').trim();

    if (!trimmed) {
      if (!required) return { valid: true, error: '' };
      return {
        valid: false,
        error: VALIDATION_MESSAGES.representativeName.required,
      };
    }

    if (trimmed.length > maxLength) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.representativeName.maxLength(maxLength),
      };
    }

    return { valid: true, error: '' };
  },

  businessRegistrationNumber(
    value: string,
    options: ValidationOptions & { maxLength?: number } = {},
  ): ValidationResult {
    const required = options.required ?? true;
    const maxLength = options.maxLength ?? LIMITS.businessRegistrationMax;
    const trimmed = (value ?? '').trim();

    if (!trimmed) {
      if (!required) return { valid: true, error: '' };
      return {
        valid: false,
        error: VALIDATION_MESSAGES.businessRegistrationNumber.required,
      };
    }

    if (trimmed.length > maxLength) {
      return {
        valid: false,
        error:
          VALIDATION_MESSAGES.businessRegistrationNumber.maxLength(maxLength),
      };
    }

    return { valid: true, error: '' };
  },

  address(
    value: string,
    options: ValidationOptions & { maxLength?: number } = {},
  ): ValidationResult {
    const required = options.required ?? true;
    const maxLength = options.maxLength ?? LIMITS.addressMax;
    const trimmed = (value ?? '').trim();

    if (!trimmed) {
      if (!required) return { valid: true, error: '' };
      return {
        valid: false,
        error: VALIDATION_MESSAGES.address.required,
      };
    }

    if (trimmed.length > maxLength) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.address.maxLength(maxLength),
      };
    }

    return { valid: true, error: '' };
  },

  content(
    value: string,
    options: ValidationOptions & {
      minLength?: number;
      maxLength?: number;
    } = {},
  ): ValidationResult {
    const required = options.required ?? true;
    const minLength = options.minLength ?? LIMITS.content.min;
    const maxLength = options.maxLength ?? LIMITS.content.max;
    const trimmed = (value ?? '').trim();

    if (!trimmed) {
      if (!required) return { valid: true, error: '' };
      return {
        valid: false,
        error: VALIDATION_MESSAGES.content.required,
      };
    }

    if (trimmed.length < minLength || trimmed.length > maxLength) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.content.length(minLength, maxLength),
      };
    }

    return { valid: true, error: '' };
  },

  receiverName(
    value: string,
    options: ValidationOptions & { maxLength?: number } = {},
  ): ValidationResult {
    const required = options.required ?? true;
    const maxLength = options.maxLength ?? LIMITS.receiverNameMax;
    const trimmed = (value ?? '').trim();

    if (!trimmed) {
      if (!required) return { valid: true, error: '' };
      return {
        valid: false,
        error: VALIDATION_MESSAGES.receiverName.required,
      };
    }

    if (trimmed.length > maxLength) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.receiverName.maxLength(maxLength),
      };
    }

    return { valid: true, error: '' };
  },

  businessCertificate(
    selectedFile: File | null | undefined,
    businessCertificateUrl: string | null | undefined,
    options: ValidationOptions = {},
  ): ValidationResult {
    const required = options.required ?? true;

    if (!required) return { valid: true, error: '' };

    if (!selectedFile && !businessCertificateUrl) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.businessCertificate.required,
      };
    }

    return { valid: true, error: '' };
  },

  isKoreanCompany(
    value: boolean | null,
    options: ValidationOptions = {},
  ): ValidationResult {
    const required = options.required ?? true;

    if (!required) return { valid: true, error: '' };

    if (value === null) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.isKoreanCompany.required,
      };
    }

    return { valid: true, error: '' };
  },

  personalInfoAgree(
    value: boolean,
    options: ValidationOptions = {},
  ): ValidationResult {
    const required = options.required ?? true;

    if (!required) return { valid: true, error: '' };

    if (!value) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.personalInfoAgree.notAgreed,
      };
    }

    return { valid: true, error: '' };
  },

  termsOfServiceAgree(
    value: boolean,
    options: ValidationOptions = {},
  ): ValidationResult {
    const required = options.required ?? true;

    if (!required) return { valid: true, error: '' };

    if (!value) {
      return {
        valid: false,
        error: VALIDATION_MESSAGES.termsOfServiceAgree.notAgreed,
      };
    }

    return { valid: true, error: '' };
  },
};
